import * as React from "react"

import { cn } from "@/lib/utils"

function Card({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card"
      className={cn(
        "bg-card text-card-foreground flex flex-col gap-6 rounded-xl border border-border py-6",
        className
      )}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className
      )}
      {...props}
    />
  )
}

/**
 * A card title's element, chosen by the host page rather than by the card.
 *
 * shadcn ships `CardTitle` as a `<div>`, which is why a 20-card feed exposed zero headings to a
 * screen reader (`GROUP-2`/`GROUP-4`, 2026-08-14) — the titles look like headings and are not.
 * `headingLevel` opts a call site into the real element.
 *
 * `1` is deliberately absent from the union. A card title is never the page's heading: the page `h1`
 * belongs to the page's own header island (`GroupPageHeading`, `SeriesHeaderDisplayer`), and a card
 * that claimed it would put a second `h1` on every list surface that renders more than one card.
 *
 * Omitting the prop is byte-identical to the previous behaviour, so existing call sites are
 * unaffected. `data-slot="card-title"` is emitted either way — nothing that styles or selects a card
 * title has to branch on the level.
 */
type CardTitleProps = React.ComponentProps<"div"> & {
  headingLevel?: 2 | 3 | 4 | 5 | 6
}

function CardTitle({ className, headingLevel, ...props }: CardTitleProps) {
  const Component = (headingLevel ? `h${headingLevel}` : "div") as "div"

  return (
    <Component
      data-slot="card-title"
      className={cn("leading-none font-semibold", className)}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-6", className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 [.border-t]:pt-6", className)}
      {...props}
    />
  )
}

export type { CardTitleProps }

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
}
